import apiClient from './apiClient';
import { formatLogMessage, LogPayload } from './logMessageFormatter';

// ============================================================================
// TYPES
// ============================================================================

export interface UserPayload {
  id: number;
  email: string;
  name: string;
  image?: string | null;
}

export type ActionCategory =
  | 'auth'
  | 'user_management'
  | 'brand'
  | 'report'
  | 'taxonomy'
  | 'import'
  | 'settings'
  | 'organization'
  | 'drawer'
  | 'performance'
  | 'alert'
  | 'other';

// URL pattern to category mapping for auto-categorization
// NOTE: Order matters! More specific patterns should come before general ones.
const URL_CATEGORY_MAP: Array<{ pattern: string; category: ActionCategory }> = [
  // Alerts (DMG alert system) - specific, keep before generic patterns
  { pattern: '/api/v1/alerts', category: 'alert' },
  { pattern: '/dmg-alerting-system', category: 'alert' },
  { pattern: '/alert-types', category: 'alert' },
  // Report configuration (MUST come before /brand and /report)
  { pattern: '/report-config', category: 'report' },
  { pattern: '/report-configuration', category: 'report' },
  // Organization patterns MUST come before /brand since org APIs often use /brand/v1/organizations/
  { pattern: '/organization', category: 'organization' },
  { pattern: '/organizations', category: 'organization' },
  // GA4 configuration (before /brand)
  { pattern: '/ga4', category: 'settings' },
  // Brand patterns (but NOT organizations or report-config which also have /brand in path)
  { pattern: '/brand/v1/brand', category: 'brand' },
  { pattern: '/brands', category: 'brand' },
  { pattern: '/weight', category: 'brand' },
  // User management
  { pattern: '/product_permissions', category: 'user_management' },
  { pattern: '/user_permissions', category: 'user_management' },
  { pattern: '/superadmin', category: 'user_management' },
  { pattern: '/admin/verify', category: 'user_management' },
  { pattern: '/admin/hold', category: 'user_management' },
  { pattern: '/admin/delete', category: 'user_management' },
  { pattern: '/admin/team', category: 'user_management' },
  { pattern: '/admin/level', category: 'user_management' },
  { pattern: '/admin/user', category: 'user_management' },
  { pattern: '/user', category: 'user_management' },
  // Auth
  { pattern: '/auth', category: 'auth' },
  { pattern: '/login', category: 'auth' },
  { pattern: '/logout', category: 'auth' },
  // Drawer structure (drawer items, NOT reports)
  { pattern: '/drawer', category: 'drawer' },
  { pattern: '/default-items', category: 'drawer' },
  { pattern: '/items', category: 'drawer' },
  { pattern: '/channels', category: 'drawer' },
  { pattern: '/applications', category: 'drawer' },
  { pattern: '/groupings', category: 'drawer' },
  // Taxonomy
  { pattern: '/txnmy', category: 'taxonomy' },
  { pattern: '/taxonomy', category: 'taxonomy' },
  { pattern: '/cdvalue', category: 'taxonomy' },
  // Import
  { pattern: '/import', category: 'import' },
  { pattern: '/upload', category: 'import' },
  { pattern: '/backfill', category: 'import' },
  { pattern: '/affluent', category: 'import' },
  // Performance
  { pattern: '/performance', category: 'performance' },
  { pattern: '/paid_ad', category: 'performance' },
  { pattern: '/account', category: 'performance' },
  // Settings (AI/ML related)
  { pattern: '/liftai', category: 'settings' },
  { pattern: '/buddies', category: 'settings' },
  { pattern: '/model', category: 'settings' },
  { pattern: '/master-memories', category: 'settings' },
  { pattern: '/workflows', category: 'settings' },
  { pattern: '/setting', category: 'settings' },
  { pattern: '/config', category: 'settings' },
];

// ============================================================================
// LEGACY FUNCTION (kept for backward compatibility)
// ============================================================================

export const actionLogTrack = async (
  payload: any,
  isError: boolean,
  user: UserPayload
) => {
  try {
    if (payload?.response) {
      delete payload.response;
    }

    let formattedPayload = payload;
    if (typeof payload === 'string') {
      try {
        formattedPayload = JSON.parse(payload);
      } catch {
        formattedPayload = { message: payload };
      }
    }

    if (!formattedPayload.message && typeof formattedPayload === 'object') {
      const formattedMessage = formatLogMessage(formattedPayload);
      formattedPayload = {
        ...formattedPayload,
        message: formattedMessage,
      };
    }

    // Auto-infer category and action type
    const category = inferCategoryFromPayload(formattedPayload);
    const actionType = inferActionTypeFromPayload(formattedPayload);

    const res = await apiClient.post(`/user/v1/admin/log/add`, {
      type: !isError ? 'INFO' : 'ERROR',
      details: {
        action: {
          category,
          type: actionType,
          description: formattedPayload.message || formatLogMessage(formattedPayload),
        },
        request: formattedPayload.url ? {
          method: formattedPayload.method,
          url: formattedPayload.url,
          statusCode: formattedPayload.status,
        } : undefined,
        user: {
          id: user.id,
          email: user.email,
          name: user.name,
          image: user.image,
        },
        payload: formattedPayload,
        result: {
          success: !isError,
          error: isError ? (formattedPayload.error || formattedPayload.message) : undefined,
        },
      },
    });
    return res;
  } catch (error) {
    console.log(error, 'log error');
  }
};

// ============================================================================
// MAIN CLASS (used throughout the app)
// ============================================================================

export class ActionLogTrack {
  user: UserPayload | null;
  
  constructor(user: UserPayload) {
    this.user = user;
  }

  currentUser() {
    return this.user;
  }

  async actionLogTrack(payload: any, isError: boolean) {
    try {
      console.log('from actionLogTrack class');

      if (!this.user) {
        console.log('action log error: No user data');
        return;
      }

      const user = this.user;

      // Remove response data if present (can be large)
      if (payload?.response) {
        delete payload.response;
      }

      // Parse if payload is stringified (existing code does JSON.stringify)
      let formattedPayload = payload;
      if (typeof payload === 'string') {
        try {
          formattedPayload = JSON.parse(payload);
        } catch {
          formattedPayload = { message: payload };
        }
      }

      // Ensure message is present
      if (!formattedPayload.message && typeof formattedPayload === 'object') {
        const formattedMessage = formatLogMessage(formattedPayload);
        formattedPayload = {
          ...formattedPayload,
          message: formattedMessage,
        };
      }

      // Auto-infer category and action type from payload
      const category = formattedPayload.category || inferCategoryFromPayload(formattedPayload);
      const actionType = formattedPayload.actionType || inferActionTypeFromPayload(formattedPayload);

      // Send enhanced format to backend
      const res = await apiClient.post(`/user/v1/admin/log/add`, {
        type: isError ? 'ERROR' : 'INFO',
        details: {
          action: {
            category,
            type: actionType,
            description: formattedPayload.message || formatLogMessage(formattedPayload),
          },
          request: formattedPayload.url ? {
            method: formattedPayload.method || 'GET',
            url: formattedPayload.url,
            statusCode: formattedPayload.status,
          } : undefined,
          user: {
            id: user.id,
            email: user.email,
            name: user.name,
            image: user.image,
          },
          payload: formattedPayload,
          result: {
            success: !isError,
            error: isError ? (formattedPayload.error || formattedPayload.message) : undefined,
          },
        },
      });
      return res;
    } catch (error) {
      console.log('action log error:', error);
    }
  }
}

// ============================================================================
// AUTO-CATEGORIZATION HELPERS
// ============================================================================

function inferCategoryFromPayload(payload: any): ActionCategory {
  const url = (payload?.url || '').toLowerCase();
  const message = (payload?.message || '').toLowerCase();

  // Check URL patterns first (most reliable)
  for (const { pattern, category } of URL_CATEGORY_MAP) {
    if (url.includes(pattern)) {
      return category;
    }
  }

  // Fallback to message-based detection (order matters - specific terms first)
  if (message.includes('login') || message.includes('logout') || message.includes('auth')) {
    return 'auth';
  }
  // Alerts - check before brand since alert messages often mention brands
  if (message.includes('alert')) {
    return 'alert';
  }
  // Report configuration must come before drawer (report config is separate from drawer reports)
  if (message.includes('report config') || message.includes('report configuration')) {
    return 'report';
  }
  // Organization must come before brand (organization APIs often mention "brand" in paths)
  if (message.includes('organization')) {
    return 'organization';
  }
  // Performance/Account - check BEFORE 'user' since messages often contain "by User"
  if (message.includes('performance') || message.includes('paid_ad') || message.includes('media platform')) {
    return 'performance';
  }
  if (message.includes('permission') || message.includes('superadmin') || message.includes('group')) {
    return 'user_management';
  }
  // Brand/Weight - check before generic 'user' check
  if (message.includes('brand') || message.includes('weight') || message.includes('goal')) {
    return 'brand';
  }
  // Drawer structure items (not report configurations)
  if (message.includes('drawer') || message.includes('sidebar') || message.includes('default item')) {
    return 'drawer';
  }
  if (message.includes('taxonomy') || message.includes('merge')) {
    return 'taxonomy';
  }
  if (message.includes('import') || message.includes('upload') || message.includes('backfill')) {
    return 'import';
  }
  if (message.includes('model') || message.includes('buddy') || message.includes('memory') || message.includes('workflow') || message.includes('ga4')) {
    return 'settings';
  }
  // User management - LAST since most messages contain "by User"
  if (message.includes('allowed') || message.includes('hold') || message.includes('deleted user')) {
    return 'user_management';
  }

  return 'other';
}

function inferActionTypeFromPayload(payload: any): string {
  const method = (payload?.method || 'GET').toUpperCase();
  const url = payload?.url || '';
  const action = payload?.action; // Some payloads have explicit action field
  
  // If explicit action is provided, use it
  if (action && typeof action === 'string') {
    return action;
  }

  // Extract resource from URL
  const urlParts = url.split('/').filter(Boolean);
  let resource = urlParts[urlParts.length - 1] || 'resource';
  
  // Clean up resource name (remove IDs, clean special chars)
  resource = resource.replace(/\d+/g, '').replace(/-/g, '_').replace(/^_+|_+$/g, '') || 'resource';

  const methodMap: Record<string, string> = {
    'POST': 'create',
    'PUT': 'update',
    'PATCH': 'update',
    'DELETE': 'delete',
    'GET': 'view',
  };

  const actionVerb = methodMap[method] || method.toLowerCase();
  return `${actionVerb}_${resource}`;
}

// ============================================================================
// API-LEVEL LOGGING HELPER
// ============================================================================

/**
 * Helper function for logging from API files without requiring context.
 * Automatically retrieves user from localStorage and logs the action.
 * 
 * @param message - Description of the action
 * @param details - Additional details (url, method, status, etc.)
 * @param isError - Whether this is an error log
 */
export async function logAdminAction(
  message: string,
  details: {
    url?: string;
    method?: string;
    status?: number;
    category?: ActionCategory;
    action?: string;
    [key: string]: any;
  } = {},
  isError: boolean = false
): Promise<void> {
  try {
    // Get user from localStorage
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.warn('logAdminAction: No user in localStorage');
      return;
    }
    
    const user = JSON.parse(userStr);
    if (!user?.id || !user?.email) {
      console.warn('logAdminAction: Invalid user data');
      return;
    }

    const payload = {
      message,
      ...details,
    };

    const category = details.category || inferCategoryFromPayload(payload);
    const actionType = details.action || inferActionTypeFromPayload(payload);

    await apiClient.post(`/user/v1/admin/log/add`, {
      type: isError ? 'ERROR' : 'INFO',
      details: {
        action: {
          category,
          type: actionType,
          description: message,
        },
        request: details.url ? {
          method: details.method || 'POST',
          url: details.url,
          statusCode: details.status,
        } : undefined,
        user: {
          id: user.id,
          email: user.email,
          name: user.name || user.email,
          image: user.image,
        },
        payload,
        result: {
          success: !isError,
          error: isError ? message : undefined,
        },
      },
    });
  } catch (error) {
    // Silent fail - don't break the main operation
    console.warn('logAdminAction failed:', error);
  }
}
